/**
 * The city name labels, and the dimmed copies of the ones the planet is hiding.
 *
 * The labels are a MapLibre symbol layer, and globe rendering depth-tests that
 * layer against the sphere: a name whose city has rotated onto the far side is
 * not drawn at all. That is the one thing the globe may not do to them — the
 * page's own bar markers only dim when they are covered — so an occluded city
 * gets a second, dimmed copy at the one spot where its name can be seen: the
 * screen pixel its position projects to, which is where the through-sphere ray
 * leaves the globe's near face. `unproject` of that pixel is the geographic
 * point to place the copy at, and `project → unproject → project` round-trips to
 * the same pixel, so a city crossing the limb hands over without a jump.
 *
 * Both layers are added here, in one place, because they share every typographic
 * literal: the behind layer is the front one with a lower `text-opacity` and the
 * declutter opt-outs below, and two owners of those literals is how they drift.
 * They also have to be added in this order — a symbol layer later in the style
 * wins a collision against an earlier one, so the behind layer is underneath and
 * a dimmed copy can never displace the real name.
 *
 * Nothing here owns a clock or a node: `update` is called by the controller's
 * existing frame loop, right after it writes the camera, and is a no-op unless a
 * copy actually moved.
 */

import type * as MaplibreGl from "maplibre-gl";

import { toAsciiUpper } from "@noko-city/shared";

import type { CityFeature, LocatedCity } from "@/components/atlas-camera";
import { cityFeatures, labelFeature } from "@/components/atlas-camera";

/** A dark halo so a name survives the coastline hairlines and the boundary lines. */
const HALO = "rgba(7, 7, 12, 0.9)";
/** The facing labels keep the ids they have always had; the copies get their own pair. */
const FRONT_SOURCE = "noko-cities";
const FRONT_LAYER = "noko-cities";
const BEHIND_SOURCE = "noko-cities-behind";
const BEHIND_LAYER = "noko-cities-behind";
/**
 * Opacity of a name whose city is behind the planet. It is meant to be read, not
 * hidden — a covered name is the answer to "where does noko cover?" — while
 * staying clearly behind the surface: 0.35 reads as recessed over the night
 * land, and at 1.0 the copy would claim a country it is not in.
 */
const BEHIND_LABEL_OPACITY = 0.35;
/**
 * How far a copy may be re-aimed before the source is rewritten. The globe turns
 * slowly; sub-pixel nudges are not worth a symbol re-placement, and skipping
 * them keeps a frame that only drifts from touching the source at all.
 */
const MIRROR_EPSILON_PX = 1.5;

/**
 * The ink's own ramp, the same clock the aircraft fade on (`INK_FADE_MS` in
 * atlas-planes): the page's selection vocabulary is 240-260 ms, and a name that
 * snaps between two inks reads as a different state rather than as the row's own
 * emphasis. MapLibre runs this on a clock of its own, so it is the one motion on
 * the page the reduced-motion CSS cannot reach — the query in `createAtlasLabels`
 * is what keeps that promise.
 */
const INK_FADE_MS = 260;

/** One copy's own name and the pixel it is aimed at. */
type Aimed = { name: string; x: number; y: number };

/**
 * Whether any of these cities is on the planet's facing side, asked of the globe
 * itself: `isLocationOccluded` is the predicate the markers dim by and the
 * behind-copies above exist for, so this can never disagree with what is on
 * screen. The idle drift asks it once a frame to decide whether the planet has to
 * turn faster to bring a city back into view.
 *
 * The answer is `true` whenever the question cannot be asked — a build without the
 * globe transform, an empty payload — so a drift that cannot tell leaves the rate
 * it has always had instead of spinning the planet up on a guess.
 *
 * Built once per map, like the label layers: the probe is one `LngLat` written in
 * place, because this runs on every animation frame.
 */
export const createCityFacing = (
  map: MaplibreGl.Map,
): ((cities: readonly LocatedCity[]) => boolean) => {
  const probe = map.unproject([0, 0]);
  return (cities) => {
    // MapLibre's own name for the carrier, read by key for the same reason the
    // label update reads it: typed on the Map class, internal in its docs.
    const transform = map["_camera"]?.transform;
    if (cities.length === 0 || typeof transform?.isLocationOccluded !== "function") return true;
    for (const city of cities) {
      probe.lng = city.lng;
      probe.lat = city.lat;
      if (!transform.isLocationOccluded(probe)) return true;
    }
    return false;
  };
};

export interface AtlasLabels {
  /**
   * Re-aim the dimmed copies for the camera that was just written. Called once
   * per controller frame; cheap when nothing moved and when no city is covered.
   */
  update(cities: readonly LocatedCity[]): void;
  /** Replace the label payload — a late or replaced API response. */
  setCities(cities: LocatedCity[]): void;
  /**
   * The city whose directory row is hovered or focused, or `null` for none: the
   * selected name keeps the accent, every other name takes the muted ink, and the
   * whole planet reads as being about that one city. The ink only — no name moves,
   * resizes or changes weight. Written by the same two calls the routes, the accent
   * aircraft and the panel are (`flyTo` / `releaseHighlight`).
   */
  setHighlight(name: string | null): void;
  /** The reduced-motion query's listener is this module's to remove. */
  dispose(): void;
}

/**
 * One name's ink: the accent the page paints every city in the moment no city is
 * the reader's own, and — while one is — that city's accent against the muted ink
 * everything else takes. Written as one data-driven expression rather than two
 * layers of names, so a highlight is a paint write and the payload keeps every
 * feature it already carries: moving the pointer between rows rebuilds nothing.
 *
 * `name` is the feature's own text, already folded by `labelFeature`, so the
 * highlight matches on exactly the string the reader sees.
 */
const labelInk = (
  highlight: string | null,
  accent: string,
  muted: string,
): NonNullable<MaplibreGl.SymbolLayerSpecification["paint"]>["text-color"] =>
  highlight === null
    ? accent
    : ["case", ["==", ["get", "name"], toAsciiUpper(highlight)], accent, muted];

/**
 * Create both label layers on an already-loaded style and return the two
 * statements the controller needs.
 *
 * `accent` and `muted` are the page's own tokens, read from the DOM by the caller:
 * the names are the only text on the planet, written in the page's orange, and a
 * name that is not the reader's city is written in the page's muted neutral —
 * never in a second accent of this module's own invention.
 */
export const createAtlasLabels = (
  map: MaplibreGl.Map,
  cities: readonly LocatedCity[],
  accent: string,
  muted: string,
): AtlasLabels => {
  const labelLayout = (): MaplibreGl.SymbolLayerSpecification["layout"] => ({
    "text-field": ["get", "name"],
    "text-font": ["Open Sans Bold"],
    "text-size": ["interpolate", ["linear"], ["zoom"], 1, 13, 8, 21, 12, 27],
    "text-anchor": "left",
    "text-offset": [1.4, 0],
  });

  /** The city the reader is on, or null. The one piece of state an ink write reads. */
  let highlight: string | null = null;

  const labelPaint = (): MaplibreGl.SymbolLayerSpecification["paint"] => ({
    "text-color": labelInk(highlight, accent, muted),
    "text-halo-color": HALO,
    "text-halo-width": 1.5,
  });

  // Underneath the facing labels (see the file note): added first, so a copy
  // loses — never wins — a collision against a real name. `text-ignore-placement`
  // is what makes "always visible" true of the copies as well: a copy is neither
  // dropped for colliding nor allowed to hold a slot another name needs, which is
  // exactly right for a label that is only ever a dimmed echo.
  map.addSource(BEHIND_SOURCE, {
    type: "geojson",
    data: { type: "FeatureCollection", features: cityFeatures([]) },
  });
  map.addLayer({
    id: BEHIND_LAYER,
    type: "symbol",
    source: BEHIND_SOURCE,
    layout: { ...labelLayout(), "text-allow-overlap": true, "text-ignore-placement": true },
    paint: { ...labelPaint(), "text-opacity": BEHIND_LABEL_OPACITY },
  });

  map.addSource(FRONT_SOURCE, {
    type: "geojson",
    data: { type: "FeatureCollection", features: cityFeatures([...cities]) },
  });
  map.addLayer({
    id: FRONT_LAYER,
    type: "symbol",
    source: FRONT_SOURCE,
    layout: labelLayout(),
    paint: labelPaint(),
  });

  /**
   * The ink's own gate, and the reason this module owns a media query: the page
   * drops every CSS transition under reduced motion, and a MapLibre transition is
   * JavaScript, so without this the names' ink would be the one thing left on the
   * page that still animated. The reduced duration is 1 ms rather than 0 for the
   * measured reason the aircraft carry: MapLibre leaves a paint change carrying a
   * zero-duration transition unrendered until something else dirties the style —
   * one millisecond is done before the next frame, so the ink still appears, in a
   * single step, which is what reduced motion asks for.
   */
  const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const applyInkFade = (): void => {
    const transition = { duration: reducedQuery.matches ? 1 : INK_FADE_MS, delay: 0 };
    map.setPaintProperty(FRONT_LAYER, "text-color-transition", transition);
    map.setPaintProperty(BEHIND_LAYER, "text-color-transition", transition);
  };
  applyInkFade();
  reducedQuery.addEventListener("change", applyInkFade);

  /**
   * Both layers carry one ink statement: a name's colour is a property of the
   * name, and the dimmed copy of it is the same name. The halos, the opacity of
   * the copies and every size stay exactly as they are.
   */
  const writeInk = (): void => {
    const ink = labelInk(highlight, accent, muted);
    map.setPaintProperty(FRONT_LAYER, "text-color", ink);
    map.setPaintProperty(BEHIND_LAYER, "text-color", ink);
  };

  const setHighlight = (name: string | null): void => {
    // A repeated hover of the row already selected writes nothing: the same ink is
    // a style change MapLibre would have to re-evaluate for every name on screen.
    if (name === highlight) return;
    highlight = name;
    writeInk();
  };

  // One probe instance, written in place: this runs on every animation frame, and
  // nothing downstream keeps the reference — the projection reads its two numbers
  // and returns. It comes from the map itself rather than a constructor the module
  // would have to be handed: the occlusion test takes the library's own `LngLat`,
  // and `unproject` returns a fresh one.
  const probe = map.unproject([0, 0]);
  /** The copies written last, for the movement test below. */
  let written: Aimed[] = [];

  /**
   * Whether this frame's copies would land on the pixels the last write used.
   * Membership is compared position by position, so a city entering or leaving
   * the covered set always rewrites, as does one that moved far enough to see.
   */
  const settled = (aimed: readonly Aimed[]): boolean =>
    aimed.length === written.length &&
    aimed.every((aim, index) => {
      const was = written[index];
      return (
        was.name === aim.name &&
        Math.abs(was.x - aim.x) <= MIRROR_EPSILON_PX &&
        Math.abs(was.y - aim.y) <= MIRROR_EPSILON_PX
      );
    });

  /**
   * One source's features. A teardown or a lost context can take the source with
   * it: the labels are best-effort and the page must never see an unhandled
   * rejection, so the failure is logged and dropped.
   */
  const write = async (id: string, features: CityFeature[]): Promise<void> => {
    try {
      await map.getSource<MaplibreGl.GeoJSONSource>(id)?.setData({
        type: "FeatureCollection",
        features,
      });
    } catch (error) {
      console.error("[noko-map] city labels update failed", error);
    }
  };

  // `setData` is asynchronous — it resolves once the worker holds the new data
  // (MapLibre's own signature is `Promise<void>`) — and the copies are re-aimed on
  // every camera frame, so a flight would otherwise keep several updates per
  // source in flight at once, each one a full GeoJSON tile reload for a dataset
  // something newer has already superseded. One writer per source keeps a single
  // update in flight and collapses whatever arrives meanwhile onto the newest
  // dataset; the final dataset handed over is always the last one queued.
  const writer = (id: string): ((features: CityFeature[]) => void) => {
    let pending: CityFeature[] | null = null;
    let draining = false;
    const drain = async (): Promise<void> => {
      draining = true;
      try {
        while (pending !== null) {
          const next = pending;
          pending = null;
          await write(id, next);
        }
      } finally {
        draining = false;
      }
    };
    return (features) => {
      pending = features;
      if (draining) return;
      void drain();
    };
  };

  const writeBehind = writer(BEHIND_SOURCE);
  const writeFront = writer(FRONT_SOURCE);

  const update = (next: readonly LocatedCity[]): void => {
    // The globe's own occlusion predicate — the same test the bar markers dim by.
    // It is read by key, not by a dot access: `_camera` is MapLibre's own name for
    // the carrier, typed on the Map class but internal in its docs, and one
    // feature is not a reason to allow every dangling underscore repo-wide. A
    // build without it, or a non-globe projection, draws nothing behind: leave
    // the source empty (the facing labels are unaffected) rather than throw.
    const transform = map["_camera"]?.transform;
    if (typeof transform?.isLocationOccluded !== "function") return;
    const aimed: Aimed[] = [];
    const features: CityFeature[] = [];
    for (const city of next) {
      probe.lng = city.lng;
      probe.lat = city.lat;
      if (!transform.isLocationOccluded(probe)) continue;
      const point = map.project(probe);
      // A projection that has not settled yet is not a reason to place a name at
      // the origin: skip this city, the next frame asks again.
      if (!Number.isFinite(point.x) || !Number.isFinite(point.y)) continue;
      const seam = map.unproject(point);
      aimed.push({ name: city.name, x: point.x, y: point.y });
      features.push(labelFeature(city, [seam.lng, seam.lat]));
    }
    if (settled(aimed)) return;
    written = aimed;
    writeBehind(features);
  };

  const setCities = (next: LocatedCity[]): void => {
    // A new payload makes the copies stale by definition, whatever the pixel
    // test would have said: names can change while the coordinates stay put.
    written = [];
    writeFront(cityFeatures(next));
  };

  return {
    update,
    setCities,
    setHighlight,
    dispose: () => reducedQuery.removeEventListener("change", applyInkFade),
  };
};
